import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { API_BASE_URL as API_URL } from '../../../config/api.js';

// Type matching backend Zod enum
export type AspectRatio = '1:1' | '2:3' | '3:2' | '3:4' | '4:3' | '4:5' | '5:4' | '9:16' | '16:9' | '21:9';

interface GenerateImagePromptParams {
    articleId: string;
    style?: string;
}

interface GenerateImagePromptResult {
    success: boolean;
    prompt: string;
}

export function useGenerateImagePrompt() {
    const { getToken } = useAuth();

    return useMutation<GenerateImagePromptResult, Error, GenerateImagePromptParams>({
        mutationFn: async ({ articleId, style }) => {
            const token = await getToken();
            const headers: HeadersInit = {
                'Content-Type': 'application/json',
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_URL}/api/image/generate-prompt`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ articleId, style }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to generate image prompt');
            }

            return response.json();
        },
    });
}

interface GenerateImageParams {
    articleId: string;
    prompt: string;
    aspectRatio?: AspectRatio;
}

interface GenerateImageResult {
    success: boolean;
    imageUrl: string;
    prompt: string;
    articleId: string;
}

export function useGenerateImage() {
    const { getToken } = useAuth();

    return useMutation<GenerateImageResult, Error, GenerateImageParams>({
        mutationFn: async ({ articleId, prompt, aspectRatio }) => {
            const token = await getToken();
            const headers: HeadersInit = {
                'Content-Type': 'application/json',
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_URL}/api/image/generate`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ articleId, prompt, aspectRatio }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to generate image');
            }

            return response.json();
        },
    });
}
